import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Music } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3a008eb2-6f58-4bf6-b16e-78d189a6aa15/artifacts/lymh6mbi_LA%20PESCHIERA%20CLUB%20WHITE%20E%20STREET.png";

const navLinks = [
  { id: 'home',    label: 'Home' },
  { id: 'prenota', label: 'Prenota' },
  { id: 'eventi',  label: 'Eventi' },
  { id: 'contatti', label: 'Contatti' },
];

const NAV_PAGE_LINKS = [
  { href: '/capodanno', label: 'Capodanno', highlight: 'capodanno' },
  { href: '/jimmys', label: "Jimmy's Club", highlight: 'jimmys' },
  { href: '/eventi-speciali', label: 'Eventi Speciali', highlight: 'special' },
  { href: '/faq', label: 'FAQ' },
  { href: '/modifica-prenotazione', label: 'Modifica Prenotazione' },
];

export default function Navbar({ highReadability, onToggleReadability }) {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive]   = useState('home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setActive(id);
    setOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'navbar-blur' : 'bg-transparent'}`}
        style={{ top: '32px' }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button onClick={() => scrollTo('home')} className="flex items-center gap-2" data-testid="nav-logo">
            <img src={LOGO_URL} alt="La Peschiera Club" className="h-10 md:h-12 object-contain" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            {navLinks.map(l => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className={`font-manrope font-600 text-sm whitespace-nowrap transition-all duration-300 ${
                  active === l.id
                    ? 'text-gold border-b-2 border-gold pb-0.5'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {l.label}
              </button>
            ))}
            {NAV_PAGE_LINKS.map(l => {
              const isHighlight = !!l.highlight;
              const isSpecial = l.highlight === 'special';
              const isCapodanno = l.highlight === 'capodanno';
              const highlightStyle = isCapodanno ? {
                background: 'linear-gradient(135deg, rgba(251,191,36,0.28), rgba(239,68,68,0.22))',
                borderColor: 'rgba(251,191,36,0.65)',
                boxShadow: '0 0 14px rgba(251,191,36,0.35)',
              } : isSpecial ? {
                background: 'linear-gradient(135deg, rgba(251,191,36,0.18), rgba(251,146,60,0.18))',
                borderColor: 'rgba(251,191,36,0.5)',
                boxShadow: '0 0 12px rgba(251,191,36,0.25)',
              } : {
                background: 'linear-gradient(135deg, rgba(236,72,153,0.18), rgba(168,85,247,0.18))',
                borderColor: 'rgba(236,72,153,0.5)',
                boxShadow: '0 0 12px rgba(236,72,153,0.25)',
              };
              return (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  className={`font-manrope font-600 text-sm whitespace-nowrap transition-all duration-300 ${
                    isHighlight
                      ? 'px-2.5 py-1 rounded-full text-white border'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  style={isHighlight ? highlightStyle : undefined}
                >
                  {isCapodanno ? '🎉 ' : ''}{l.label}
                </a>
              );
            })}
            <button
              data-testid="readability-toggle"
              onClick={onToggleReadability}
              className={`text-sm font-manrope font-600 px-3 py-1.5 rounded-full border transition-all ${
                highReadability
                  ? 'border-aqua text-aqua bg-aqua/10'
                  : 'border-white/20 text-white/50 hover:border-white/40'
              }`}
              title="Alta leggibilità"
            >
              A+
            </button>
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={onToggleReadability}
              data-testid="readability-toggle-mobile"
              className={`text-sm font-manrope font-600 px-3 py-1.5 rounded-full border transition-all ${
                highReadability ? 'border-aqua text-aqua' : 'border-white/20 text-white/50'
              }`}
            >
              A+
            </button>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(!open)}
              className="text-white p-2"
            >
              {open ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden navbar-blur border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map(l => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-left font-manrope font-600 text-xl text-white hover:text-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
            {NAV_PAGE_LINKS.map(l => {
              const isHighlight = !!l.highlight;
              const isSpecial = l.highlight === 'special';
              const isCapodanno = l.highlight === 'capodanno';
              return (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  className={`text-left font-manrope font-600 text-xl transition-colors ${
                    isHighlight
                      ? (isCapodanno ? 'hover:text-amber-200' : isSpecial ? 'hover:text-amber-300' : 'text-pink-400 hover:text-pink-300')
                      : 'text-white hover:text-gold'
                  }`}
                  style={isHighlight && (isSpecial || isCapodanno) ? { color: '#fbbf24' } : undefined}
                >
                  {isCapodanno ? `🎉 ${l.label}` : isHighlight ? `★ ${l.label}` : l.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}
